import React, { useMemo, useState, useEffect } from 'react';
import { buildMailtoUrl, fillTemplate } from '../lib/mailto';

const MARK_SAFETY_MS = 5000;

export default function LenderCard({ lender, firstName, bcc, templates, onMarkSent, onMarkUnsent, onDidntWork }) {
  const [mailtoOpened, setMailtoOpened] = useState(false);
  const [justSent, setJustSent] = useState(null);

  const vars = useMemo(() => ({
    client_name: firstName || '',
    first_name: firstName || '',
    lender_name: lender.lenderName,
    agreement_number: lender.agreementNumber || '',
  }), [firstName, lender]);

  const subject = fillTemplate(templates?.perLenderSubject, vars) || `Request to remain represented by Fair — ${lender.lenderName}`;
  const body = fillTemplate(templates?.perLenderBody, vars) || defaultBody(vars);

  const { href, truncated } = useMemo(() => buildMailtoUrl({
    to: lender.lenderEmail,
    subject,
    body,
    bcc,
  }), [lender.lenderEmail, subject, body, bcc]);

  useEffect(() => {
    const onFocus = () => {
      if (mailtoOpened) {
        const target = document.getElementById('mark-sent-btn');
        if (target) target.focus();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [mailtoOpened]);

  const handleOpen = () => {
    setMailtoOpened(true);
    if (truncated && onDidntWork) {
      onDidntWork();
      return;
    }
    window.location.href = href;
  };

  const handleMark = () => {
    const when = Date.now();
    setJustSent(when);
    onMarkSent();
    setTimeout(() => setJustSent((prev) => prev === when ? null : prev), MARK_SAFETY_MS);
  };

  if (!lender.emailValid) {
    return (
      <div className="card stack">
        <span className="badge warn">Can't send automatically</span>
        <h2 className="h2">{lender.lenderName}</h2>
        <p className="muted">We'll handle this lender manually for you — no action needed.</p>
        <button className="btn full" onClick={onMarkSent}>Acknowledge and continue</button>
      </div>
    );
  }

  return (
    <div className="card stack">
      <span className="badge lender">Lender {lender.lenderName}</span>
      <h2 className="h2">Confirm your authority with {lender.lenderName}</h2>
      <p className="muted">Tap below to open your email app with a pre-filled message ready to review and send. Once sent, return here and tap <strong>I've Sent It</strong>.</p>

      <a href={href} className="btn full" onClick={handleOpen}>Open email to {lender.lenderName}</a>

      <button
        id="mark-sent-btn"
        className="btn full secondary"
        style={{ borderColor: mailtoOpened ? 'var(--color-success)' : undefined }}
        onClick={handleMark}
      >
        I've sent it — mark as sent
      </button>

      <div className="row" style={{ gap: 24, marginTop: 4 }}>
        {justSent ? (
          <button className="link-action small" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={onMarkUnsent}>
            Haven't sent it yet — go back
          </button>
        ) : null}
        <button className="link-action small" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={onDidntWork}>
          Didn't open? Show me another way
        </button>
      </div>
    </div>
  );
}

// Default per-lender email body — matches the "Email Content (Update)" block
// in the May-2026 Declaration Client Email Updates brief. Note the legal
// naming: clients are authorising "Reclaims 4 U Ltd t/a Fair", not just
// "Fair Inc" (FCA reference number FRN 830181 is held by Reclaims 4 U Ltd).
function defaultBody(v) {
  return [
    'To whom it may concern,',
    '',
    'I am writing regarding my vehicle finance agreement and any related claim or complaint.',
    '',
    'I confirm that I have authorised Reclaims 4 U Ltd t/a Fair to act on my behalf in relation to my motor finance claim, including matters relating to undisclosed commission.',
    '',
    'Please correspond directly with Reclaims 4 U Ltd t/a Fair regarding this matter where appropriate.',
    '',
    'If you require any further information from me, please let me know.',
    '',
    'Kind regards,',
    v.first_name || v.client_name || '',
  ].join('\n');
}
